import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import type { BimWorkOrderChangeOtherPriceData, SectionEnum } from 'gql/graphql';
import { getDefaultFilterCleaner, usePageFilterSaver, withDefaultTransformer } from 'hooks/usePageFilterSaver';
import { OTHER_TABLE_GROUP_NAME } from 'pages/Project/BimProject/WorkOrder/types';

import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import { bimWorkOrderChangeOtherPricesGql } from '../bimWorkOrderChangeOthersQueries';

interface IUseWorkOrderChangeOtherPrice {
    workOrderChangeId: number;
    workOrderRootObjectId: number | undefined;
    workOrderChangeRootObjectId: number | undefined;
    withEffect?: boolean;
}

type TGroupedItem =
    | BimWorkOrderChangeOtherPriceData
    | { id: SectionEnum | string; name: string; isGroupTitle: boolean };

const getGroupedItems = (itemsArr: BimWorkOrderChangeOtherPriceData[] | undefined | null): TGroupedItem[] => {
    if (!itemsArr) {
        return [];
    }

    const withGroups = itemsArr.reduce(
        (acc: Record<SectionEnum, TGroupedItem[]>, item) => {
            const groupName = item.section;

            if (groupName) {
                if (!acc[groupName]) {
                    acc[groupName] = [{ id: groupName, name: OTHER_TABLE_GROUP_NAME[groupName], isGroupTitle: true }];
                }
                acc[groupName].push({
                    ...item,
                    id:
                        item.workOrderChangeOtherPriceId ??
                        `${item.workOrderChangeOtherPriceId}_${item.workOrderOtherPriceId}`,
                });
            }

            return acc;
        },
        {} as Record<SectionEnum, TGroupedItem[]>,
    );

    return Object.values(withGroups).flat();
};

export const useWorkOrderChangeOtherPriceList = (props: IUseWorkOrderChangeOtherPrice) => {
    const dispatch = useDispatch();
    const { workOrderChangeId, workOrderRootObjectId, workOrderChangeRootObjectId, withEffect = true } = props;

    const [filterWhere, setFilterWhere] = useState({});

    const changeFilterHandler = useCallback(
        ({ fieldName, checkedValues }: { fieldName: string; checkedValues: string[] }) => {
            setFilterWhere((prevValue) => ({
                ...prevValue,
                [fieldName]: checkedValues?.length
                    ? {
                          in: checkedValues,
                      }
                    : undefined,
            }));
        },
        [],
    );

    const variables = useMemo(
        () => ({
            workOrderChangeId,
            workOrderRootObjectId,
            workOrderChangeRootObjectId,
            where: filterWhere,
        }),
        [filterWhere, workOrderChangeId, workOrderChangeRootObjectId, workOrderRootObjectId],
    );

    const { canSendRequest } = usePageFilterSaver(
        useMemo(
            () => ({
                filterSetter: withDefaultTransformer(
                    changeFilterHandler,
                    `others_${workOrderRootObjectId}_${workOrderChangeRootObjectId}`,
                    getDefaultFilterCleaner({
                        changeFilterHandler,
                        setFiltersWhere: setFilterWhere,
                    }),
                ),
            }),
            [changeFilterHandler, workOrderChangeRootObjectId, workOrderRootObjectId],
        ),
    );

    const [data, status, , request] = useRequest({
        requestId: 'bimWorkOrderChangeOtherPriceList',
        query: bimWorkOrderChangeOtherPricesGql,
        variables,
        withEffect: withEffect && canSendRequest,
    });

    const tableItems = useMemo(
        () => getGroupedItems(data?.bimWorkOrderChangeOtherPrices?.items),
        [data?.bimWorkOrderChangeOtherPrices?.items],
    );

    const isLoading = status === RequestStatus.FETCHING;

    const totalItems = useMemo(
        () => data?.bimWorkOrderChangeOtherPrices?.totalCount,
        [data?.bimWorkOrderChangeOtherPrices?.totalCount],
    );

    const fetchedItemsCounter = useMemo(
        () => data?.bimWorkOrderChangeOtherPrices?.items?.length,
        [data?.bimWorkOrderChangeOtherPrices?.items?.length],
    );

    const refreshWorkOrderOtherPriceList = useCallback(() => {
        dispatch(request(variables));
    }, [dispatch, request, variables]);

    return {
        tableItems,
        isLoading,
        totalItems,
        fetchedItemsCounter,
        changeFilterHandler,
        refreshWorkOrderOtherPriceList,
    };
};
